/* eslint-disable @typescript-eslint/explicit-function-return-type */

import { Option } from '@Components/types/options';
import { ITRequestFormResponse } from '@Types/api/form';
import { TypeDict, User } from '@Types/index';
import { EMPTY_FIELD_PLACEHOLDER } from '@Constants';
import { ItRequisitionTypeCodes } from '@Enum/api/ItRequisitionTypeCodes.enum';

import { ItRequisitionStatusCodes } from '@Enum/api/ItRequisitionStatusCodes';
import { userTypes } from '../../isNewUser.enum';
import {
  accountModuleOptions,
  moduleOptions,
  requiredRoleOptions,
} from '../../fieldsOptions';
import { DEFAULT_REPLACEMENT_FEE, DEFAULT_CURRENCY } from '../constants';

import styles from './view-form.module.css';
import { parseIntCalls } from '../utils';

const prepareSource = (
  data: ITRequestFormResponse,
  findRequestType: (requestTypeID: number) => TypeDict | undefined,
  organizations: Option[],
  projects: Option[],
  itAccessories: Option[],
  users: User[],
  currentUser: User,
  documentId: string,
  existingValue: string,
  handleDownloadClick: () => void
) => {
  const { reqItemId } = data;

  const requestTypeDesc = findRequestType(reqItemId)!.codeDesc;

  const userName = users.find(
    (item) => item.empNumber === data.empNumber
  )!.empName;
  const isUserMatched = data.empNumber === currentUser.empNumber;
  const requestedForPostfix = isUserMatched ? ' (Myself)' : '';
  const requestedFor = `${userName}${requestedForPostfix}`;

  const commonFields = {
    'Requested For': requestedFor,
    Status: data.statusId,
    'Requisition Item': requestTypeDesc,
  };

  if (reqItemId === ItRequisitionTypeCodes.NEW_SIM_CARD) {
    return {
      ...commonFields,
      Currency: DEFAULT_CURRENCY,
      'Contact Number': data.personalMobile || EMPTY_FIELD_PLACEHOLDER,
      Remarks: data.comments || EMPTY_FIELD_PLACEHOLDER,
    };
  }

  if (reqItemId === ItRequisitionTypeCodes.BUILD_SMART_ACCESS) {
    const userInformation = userTypes[data.isNewUser];

    const modulesStr = data.modules
      .map(
        (moduleCode) =>
          moduleOptions.find((item) => item.value === moduleCode)?.label || ''
      )
      .join(', ');

    const accountsModuleDesc = data.acctModules
      .map(
        (moduleCode) =>
          accountModuleOptions.find((item) => item.value === moduleCode)
            ?.label || ''
      )
      .join(', ');

    const [organizationCode] = data.organizations;
    const organizationDesc = organizations.find(
      (item) => item.value === organizationCode
    )?.label;

    const projectsStr = data.projects
      .map(
        (projectCode) =>
          projects.find((item) => item.value === projectCode)?.label || ''
      )
      .join(', ');

    const requiredRole = requiredRoleOptions.find(
      (item) => item.value === data.role
    )?.label;

    return {
      ...commonFields,
      'Contact Number': data.personalMobile || EMPTY_FIELD_PLACEHOLDER,
      'User Information': userInformation,
      Module: modulesStr || EMPTY_FIELD_PLACEHOLDER,
      'Accounts Module': accountsModuleDesc || EMPTY_FIELD_PLACEHOLDER,
      Organization: organizationDesc || EMPTY_FIELD_PLACEHOLDER,
      Projects: projectsStr || EMPTY_FIELD_PLACEHOLDER,
      'Required Role': requiredRole || EMPTY_FIELD_PLACEHOLDER,
      Remarks: data.comments || EMPTY_FIELD_PLACEHOLDER,
    };
  }

  if (reqItemId === ItRequisitionTypeCodes.EMAIL) {
    const documentEl = documentId ? (
      <div className={styles.linkDownload} onClick={handleDownloadClick}>
        {data.fileName}
      </div>
    ) : (
      'No attachments'
    );

    return {
      ...commonFields,
      'Email ID': data.username || EMPTY_FIELD_PLACEHOLDER,
      'Email Signature': data.accessories || EMPTY_FIELD_PLACEHOLDER,
      'Contact Number': data.personalMobile || EMPTY_FIELD_PLACEHOLDER,
      Document: documentEl,
      Remarks: data.comments || EMPTY_FIELD_PLACEHOLDER,
    };
  }

  if (reqItemId === ItRequisitionTypeCodes.SIM_ADDON_REQUEST) {
    return {
      ...commonFields,
      Mobile: data.personalMobile || EMPTY_FIELD_PLACEHOLDER,
      'Recommend Package': data.recommendPackage || EMPTY_FIELD_PLACEHOLDER,

      ...(data.statusId === ItRequisitionStatusCodes.AWAITING_APPROVAL && {
        'Existing Package': existingValue || EMPTY_FIELD_PLACEHOLDER,
      }),
      'Replacement Fee': DEFAULT_REPLACEMENT_FEE,
      Currency: DEFAULT_CURRENCY,
      Remarks: data.comments || EMPTY_FIELD_PLACEHOLDER,
    };
  }

  if (reqItemId === ItRequisitionTypeCodes.LOST_SIM_CARD) {
    return {
      ...commonFields,
      Mobile: data.personalMobile || EMPTY_FIELD_PLACEHOLDER,
      'Replacement Fee': DEFAULT_REPLACEMENT_FEE,
      Currency: DEFAULT_CURRENCY,
      Remarks: data.comments || EMPTY_FIELD_PLACEHOLDER,
    };
  }

  if (reqItemId === ItRequisitionTypeCodes.ASSET_STATUS_CHANGE) {
    return {
      ...commonFields,
      'Contact Number': data.personalMobile || EMPTY_FIELD_PLACEHOLDER,
      Remarks: data.comments || EMPTY_FIELD_PLACEHOLDER,
    };
  }

  if (reqItemId === ItRequisitionTypeCodes.INTERNATIONAL_CALLS) {
    const { codeCountry, intCallNumber } = parseIntCalls(data.personalMobile);

    return {
      ...commonFields,
      'Country Code': `+${codeCountry}`,
      'International Number': intCallNumber || EMPTY_FIELD_PLACEHOLDER,
      Remarks: data.comments || EMPTY_FIELD_PLACEHOLDER,
    };
  }

  if (reqItemId === ItRequisitionTypeCodes.ACCESSORIES) {
    const findedLabels = data.accessories
      .split(',')
      .map(
        (accessoryValue) =>
          itAccessories.find(
            (accessory) => accessory.value === accessoryValue.trim()
          )?.label
      );

    return {
      ...commonFields,
      'Contact Number': data.personalMobile || EMPTY_FIELD_PLACEHOLDER,
      Accessories: findedLabels.join(','),
      Remarks: data.comments || EMPTY_FIELD_PLACEHOLDER,
    };
  }

  return [];
};

const fieldSource = (
  data: ITRequestFormResponse,
  findRequestType: (requestTypeID: number) => TypeDict | undefined,
  organizations: Option[],
  projects: Option[],
  itAccessories: Option[],
  users: User[],
  currentUser: User,
  documentId: string,
  existingValue: string,
  handleDownloadClick: () => void
): Array<{ label: string; value: string | number | boolean | JSX.Element }> => {
  const source = prepareSource(
    data,
    findRequestType,
    organizations,
    projects,
    itAccessories,
    users,
    currentUser,
    documentId,
    existingValue,
    handleDownloadClick
  );

  return Object.entries(source).map((item) => ({
    label: item[0],
    value: item[1],
  }));
};

export default fieldSource;
